import { useMemo } from 'react';

import { useCommandRegistry } from '../components/command-registry-provider';
import type { Command } from '../types/command';
import { buildSnapRegistry } from '../utils/snap';

/**
 * Walks the flat registry once and returns a `Map<snapKey, command>`
 * for every command that opted in to snap support via `snap: 'foo'`.
 *
 * Memoised against the `commands` reference, which only changes on
 * real register/unregister mutations. The hook does no work per
 * keystroke -- the autocomplete component is responsible for calling
 * `parseSnapInput` against this map on input change.
 *
 * Snap precedence on key collision is "last write wins" relative to
 * the order `Object.values(commands)` returns them. The order is
 * stable across renders (modern JS engines preserve insertion order
 * for string-keyed records); for the rare case of two commands
 * declaring the same snap, the second-registered one wins. Throwing
 * an invariant would also be defensible -- we lean toward silently
 * shadowing because the snap UX gracefully handles missing keys
 * (the listbox shows "no matching snap"), so an accidental shadow is
 * recoverable in a way an ID collision isn't.
 */
export function useSnapRegistry(): Map<string, Command> {
  const { commands } = useCommandRegistry();
  return useMemo(() => buildSnapRegistry(Object.values(commands)), [commands]);
}
